import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { statsAPI, ideasAPI, collaborationsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Lightbulb, 
  MessageSquare, 
  ThumbsUp, 
  Store, 
  Plus,
  TrendingUp,
  Clock,
  ArrowRight,
  Bell,
  CheckCircle,
  X
} from 'lucide-react';

export const DashboardPage = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [myIdeas, setMyIdeas] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [statsRes, ideasRes, invitesRes] = await Promise.all([
        statsAPI.getDashboard(),
        ideasAPI.getMy(),
        collaborationsAPI.getPending()
      ]);
      setStats(statsRes.data);
      setMyIdeas(ideasRes.data.slice(0, 5));
      setPendingInvites(invitesRes.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvite = async (inviteId) => {
    try {
      await collaborationsAPI.accept(inviteId);
      toast.success('Collaboration accepted!');
      setPendingInvites(prev => prev.filter(i => i.id !== inviteId));
    } catch (error) {
      toast.error('Failed to accept invitation');
    }
  };

  const handleDeclineInvite = async (inviteId) => {
    try {
      await collaborationsAPI.decline(inviteId);
      toast.success('Invitation declined');
      setPendingInvites(prev => prev.filter(i => i.id !== inviteId));
    } catch (error) {
      toast.error('Failed to decline invitation');
    }
  };

  const statCards = [
    { 
      label: 'Total Points', 
      value: user?.points || 0, 
      icon: TrendingUp, 
      color: 'text-orange-500',
      bgColor: 'bg-orange-50'
    },
    { 
      label: 'My Ideas', 
      value: stats?.ideas_count || 0, 
      icon: Lightbulb, 
      color: 'text-indigo-500',
      bgColor: 'bg-indigo-50'
    },
    { 
      label: 'Comments Made', 
      value: stats?.comments_count || 0, 
      icon: MessageSquare, 
      color: 'text-green-500',
      bgColor: 'bg-green-50'
    },
    { 
      label: 'Upvotes Received', 
      value: stats?.total_upvotes || 0, 
      icon: ThumbsUp, 
      color: 'text-blue-500',
      bgColor: 'bg-blue-50'
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Welcome Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl text-slate-900">
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-slate-600 mt-1">Here's what's happening with your business ideas.</p>
        </div>
        <Button 
          className="gradient-secondary text-white btn-animate"
          onClick={() => navigate('/ideas/new')}
          data-testid="new-idea-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          New Idea
        </Button>
      </div>

      {/* Pending Invitations */}
      {pendingInvites.length > 0 && (
        <Card className="border-orange-200 bg-orange-50/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Bell className="w-5 h-5 text-orange-500" />
              Collaboration Invites
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {pendingInvites.map((invite) => (
              <div 
                key={invite.id} 
                className="flex items-center justify-between bg-white p-3 rounded-lg border border-orange-100"
                data-testid={`invite-${invite.id}`}
              >
                <div>
                  <p className="font-medium text-slate-900">{invite.idea_title}</p>
                  <p className="text-sm text-slate-600">Invited by {invite.inviter_name}</p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    className="gradient-primary text-white"
                    onClick={() => handleAcceptInvite(invite.id)}
                    data-testid={`accept-invite-${invite.id}`}
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Accept
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleDeclineInvite(invite.id)}
                    data-testid={`decline-invite-${invite.id}`}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="card-hover" data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-600">{stat.label}</p>
                    <p className={`font-impact text-3xl mt-1 ${stat.color}`}>
                      {stat.value.toLocaleString()}
                    </p>
                  </div>
                  <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                    <Icon className={`w-5 h-5 ${stat.color}`} strokeWidth={1.5} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* My Ideas */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-heading">My Business Ideas</CardTitle>
              <Link to="/browse" className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1">
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </CardHeader>
            <CardContent>
              {myIdeas.length === 0 ? (
                <div className="text-center py-8">
                  <Lightbulb className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600 mb-4">You haven't created any ideas yet.</p>
                  <Button 
                    className="gradient-primary text-white"
                    onClick={() => navigate('/ideas/new')}
                  >
                    Create Your First Idea
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {myIdeas.map((idea) => (
                    <Link 
                      key={idea.id} 
                      to={`/ideas/${idea.id}`}
                      className="block p-4 rounded-lg border border-slate-200 hover:border-indigo-200 hover:bg-indigo-50/50 transition-all"
                      data-testid={`idea-${idea.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-slate-900 truncate">{idea.title}</h3>
                          <p className="text-sm text-slate-600 line-clamp-1 mt-1">{idea.description}</p>
                          <div className="flex items-center gap-3 mt-2">
                            <Badge variant="secondary" className="text-xs">
                              {idea.category}
                            </Badge>
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(idea.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <Badge 
                          className={`shrink-0 ${
                            idea.status === 'planned' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-slate-100 text-slate-700'
                          }`}
                        >
                          {idea.status}
                        </Badge>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="space-y-6">
          {/* Points Card */}
          <Card className="gradient-secondary text-white overflow-hidden relative">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
            <CardContent className="pt-6 relative z-10">
              <p className="text-orange-100 text-sm uppercase tracking-wider">Your Balance</p>
              <p className="font-impact text-5xl mt-2">{user?.points || 0}</p>
              <p className="text-orange-100 mt-1">Points available</p>
              <Button 
                className="mt-4 bg-white text-orange-600 hover:bg-orange-50 w-full"
                onClick={() => navigate('/marketplace')}
                data-testid="browse-services-btn"
              >
                <Store className="w-4 h-4 mr-2" />
                Browse Services
              </Button>
            </CardContent>
          </Card>

          {/* Quick Links */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => navigate('/browse')}
                data-testid="browse-ideas-btn"
              >
                <Lightbulb className="w-4 h-4 mr-2 text-indigo-500" />
                Browse Community Ideas
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => navigate('/leaderboard')}
                data-testid="view-leaderboard-btn"
              >
                <TrendingUp className="w-4 h-4 mr-2 text-orange-500" />
                View Leaderboard
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => navigate('/profile')}
                data-testid="offer-services-btn"
              >
                <Store className="w-4 h-4 mr-2 text-green-500" />
                Offer Your Services
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
